/**
 * Templates.
 *
 * Deliberately not a designer. The approved `.docx` files are the source of
 * truth and arrive with the deployment; what an administrator controls here is
 * which families Sales may choose and which version new proposals are created
 * against.
 */
import { useEffect, useState } from 'react';
import { endpoints, type AdminTemplate } from '../../lib/api';
import { Alert, Button, Card, Select, Skeleton, formatDateTime } from '../../components/ui';
import { AdminTable, Empty, StatusPill } from './shared';

export function TemplatesAdmin({ onError }: { onError(message: string): void }) {
  const [templates, setTemplates] = useState<AdminTemplate[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    endpoints
      .adminTemplates()
      .then((result) => setTemplates(result.templates))
      .catch((caught) => onError(String(caught)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (templateId: string, action: () => Promise<unknown>, message: string) => {
    setBusyId(templateId);
    setNotice(null);
    try {
      await action();
      await load();
      setNotice(message);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusyId(null);
    }
  };

  if (!templates) return <Skeleton className="h-64 w-full" />;

  return (
    <Card
      title="Proposal templates"
      description="The approved Word templates. Seeded with the deployment; activate, deactivate and choose the default version here."
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      {templates.length === 0 ? (
        <Empty>No templates registered. Run the seed.</Empty>
      ) : (
        <AdminTable
          minWidth={980}
          headers={[
            'Name',
            'Version',
            'Status',
            'Source file',
            'Processed file',
            'Created',
            'Updated',
            'Actions',
          ]}
        >
          {templates.map((template) => (
            <tr key={template.templateId} className="align-top">
              <td className="px-5 py-3">
                <span className="font-medium text-ink">{template.name}</span>
                <span className="block text-[12px] text-muted">{template.templateId}</span>
              </td>
              <td className="px-5 py-3">
                {template.versions.length > 1 ? (
                  <Select
                    aria-label={`Default version for ${template.name}`}
                    value={template.currentVersion}
                    disabled={busyId === template.templateId}
                    onChange={(event) =>
                      void run(
                        template.templateId,
                        () =>
                          endpoints.setTemplateDefaultVersion(
                            template.templateId,
                            event.target.value,
                          ),
                        `${template.name} now defaults to version ${event.target.value}.`,
                      )
                    }
                  >
                    {template.versions.map((version) => (
                      <option key={version.version} value={version.version}>
                        {version.version}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className="tabular-nums text-ink">{template.currentVersion}</span>
                )}
              </td>
              <td className="px-5 py-3">
                <StatusPill status={template.status} />
              </td>
              <td className="px-5 py-3 text-[12px] text-muted">
                <span className="break-all">{template.sourceFile}</span>
                <span
                  className="mt-0.5 block font-mono text-[11px]"
                  title={template.sourceSha256}
                >
                  sha256 {template.sourceSha256.slice(0, 12)}…
                </span>
              </td>
              <td className="px-5 py-3 text-[12px] text-muted">
                <span className="break-all">{template.processedFile ?? '—'}</span>
              </td>
              <td className="px-5 py-3 text-[12px] text-muted">
                {formatDateTime(template.createdAt)}
              </td>
              <td className="px-5 py-3 text-[12px] text-muted">
                {formatDateTime(template.updatedAt)}
              </td>
              <td className="px-5 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busyId === template.templateId}
                  onClick={() =>
                    void run(
                      template.templateId,
                      () =>
                        endpoints.setTemplateStatus(
                          template.templateId,
                          template.status === 'ACTIVE' ? 'RETIRED' : 'ACTIVE',
                        ),
                      template.status === 'ACTIVE'
                        ? `${template.name} deactivated. Existing proposals are unaffected.`
                        : `${template.name} activated.`,
                    )
                  }
                >
                  {template.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </Button>
              </td>
            </tr>
          ))}
        </AdminTable>
      )}

      <p className="mt-4 text-[12px] text-muted">
        Deactivating hides a family from New Proposal. Proposals already using it stay
        renderable — they are pinned to the version they were created with, and withdrawing a
        family should not strand work in progress. Changing the default version affects new
        proposals only, for the same reason.
      </p>
    </Card>
  );
}
